// lib
import { useMemo } from 'react'
import PropTypes from 'prop-types'

// components
import { Button } from 'primereact/button'

// styles
import style from '@aytm/app/stats/page/sidebar/components/styles/SidebarPopupWarning.module.scss'

const SidebarPopupWarning = ({
  features = [],
  currentPanel,
  message,
  isSpecial,
  isReportVirtual,
  isReportXpert,
  isQAMode = false,
  disabled,
  onModeChange
}) => {
  const currentState = useMemo(() => {
    const isDisabled = 0 < features.length ? features.every(item => item.disabled) : disabled

    if (isDisabled) {
      const isLocked = 0 < features.length ? features.every(item => item.locked) : disabled

      if (isLocked) return 'locked'

      const isAvailable =
        0 < features.length &&
        features.every((item, i) => {
          const prev = features.slice(0, i).find(_item => _item.available)
          const available = item.available && (!prev || prev?.onPackage?.id == item?.onPackage?.id)
          return available || item.hidden
        })

      if (isAvailable) return 'available'

      return 'disabled'
    }

    return null
  }, [features, disabled])

  const availableFeature = useMemo(
    () => ('available' == currentState ? features.find(item => item.available) : null),
    [features, currentState]
  )

  // a state with nothing to say draws no bubble at all
  const hasContent =
    isReportVirtual ||
    isReportXpert ||
    isQAMode ||
    isSpecial ||
    'locked' == currentState ||
    Boolean(availableFeature?.onPackage) ||
    Boolean(message)

  return null !== currentState && hasContent ? (
    <div className={style.component}>
      {isReportVirtual || isReportXpert ? (
        <div>
          {'data-centrifuge' == currentPanel
            ? 'Please switch to the "Original - as fielded" or other custom reports in order to see data cleaning results'
            : 'Please switch to the "Original - as fielded" or other custom reports in order to use these features'}
        </div>
      ) : isQAMode ? (
        <div>Please exit &quot;QA mode&quot; to use these features</div>
      ) : isSpecial ? (
        <>
          <span>Please switch to the Quant report to use these features</span>
          <Button
            className="p-button-rounded p-button-blue p-button-sm"
            label="Switch to Quant report"
            icon={<i className="icon-aytmfd-quant-report-v2" />}
            iconPos="left"
            onClick={() => onModeChange('charts')}
          />
        </>
      ) : 'locked' == currentState ? (
        <div>
          {'data-centrifuge' == currentPanel
            ? 'Please login/join the team to see data cleaning results'
            : 'Please login/join the team to unlock all features of the stat report'}
        </div>
      ) : 'available' == currentState && availableFeature?.onPackage ? (
        <div>
          {/* PME2 (AYTM-11321): render server-authored `text` verbatim; guard the icon, which the
              already-on-Enterprise Contact-Us variant omits. `id` stays the CSS theme-color hook. */}
          <a
            className={`stat-upgrade-button ${availableFeature.onPackage.id}`}
            href={availableFeature.onPackage.upgradeURL}
            target="_blank"
            rel="noreferrer"
          >
            {availableFeature.onPackage.icon ? (
              <span className="stat-upgrade-icon">
                <i className={`icon-aytmfd-${availableFeature.onPackage.icon}`}></i>
              </span>
            ) : null}
            <span className="stat-upgrade-text" dangerouslySetInnerHTML={{ __html: availableFeature.onPackage.text }} />
          </a>
        </div>
      ) : null}
      {message ? <div dangerouslySetInnerHTML={{ __html: message }} /> : null}
    </div>
  ) : null
}

SidebarPopupWarning.propTypes = {
  features: PropTypes.array,
  currentPanel: PropTypes.string,
  responses: PropTypes.number,
  message: PropTypes.string,
  isSpecial: PropTypes.bool,
  isReportVirtual: PropTypes.bool,
  isReportXpert: PropTypes.bool,
  isQAMode: PropTypes.bool,
  disabled: PropTypes.bool,
  onModeChange: PropTypes.func
}

SidebarPopupWarning.displayName = 'SidebarPopupWarning'

export default SidebarPopupWarning
